import { useMemo } from 'react'
import useSWR from 'swr'
import API from 'utils/api'
import { useToken } from 'app/hooks'

const KEY = '/admin/services/users'

export interface ServiceAvailability {
	goms: boolean
	want: boolean
}

export interface ServiceUser {
	id: number
	name: string
	email: string
	deptId: number | null
	deptName: string | null
	groupId: number | null
	groupName: string | null
	type: number | null
	services: ServiceAvailability
}

export interface ServiceUsersResponse {
	total: number
	limit: number
	offset: number
	items: ServiceUser[]
}

export interface UseServiceUsersParams {
	limit?: number
	offset?: number
	search?: string
	type?: number | null
	groupId?: number | null
}

export function useServiceUsers(params: UseServiceUsersParams) {
	const { data: token } = useToken()

	const queryKey = useMemo(() => {
		const searchParams = new URLSearchParams()

		if (typeof params?.limit !== 'undefined') {
			searchParams.set('limit', `${params.limit}`)
		}

		if (typeof params?.offset !== 'undefined') {
			searchParams.set('offset', `${params.offset}`)
		}

		if (params?.search) {
			searchParams.set('search', params.search)
		}

		if (typeof params?.type !== 'undefined' && params?.type !== null && params?.type !== ('' as any)) {
			searchParams.set('type', `${params.type}`)
		}

		if (typeof params?.groupId !== 'undefined' && params?.groupId !== null && !Number.isNaN(params?.groupId as any)) {
			searchParams.set('groupId', `${params.groupId}`)
		}

		const qs = searchParams.toString()
		return qs ? `${KEY}?${qs}` : KEY
	}, [params?.limit, params?.offset, params?.search, params?.type, params?.groupId])

	const shouldFetch = token ? queryKey : null

	const { data, error, isValidating, mutate } = useSWR<ServiceUsersResponse>(
		shouldFetch,
		async (url: string) => {
			const response = await API.get(url, token + '', {})
			return response.result || { total: 0, limit: params?.limit || 0, offset: params?.offset || 0, items: [] }
		},
		{ revalidateOnFocus: true }
	)

	const isLoading = !data && !error

	return {
		data,
		error,
		isLoading,
		isValidating,
		mutate,
	}
}

export default useServiceUsers
